import { action, runInAction, makeObservable, observable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import axios, { AxiosError, CancelTokenSource } from 'axios';

import { reverse } from 'named-urls';

import Logger from 'utils/logger';

import API from 'utils/api';
import { postMessage } from 'utils/events/broadcast';
import { EventType } from 'utils/events/constants';
import { ChangedLibraryControlsPayload } from 'utils/events/types';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { EntityValueType } from 'utils/api/modules/entities/entities.types';
import { ControlEvidenceRow, ControlPutInPlace } from 'utils/api/modules/risks';
import { isConflict, isNotFound } from 'utils/api/errors';
import { conflictDetail, fieldErrorsOf } from 'utils/library';

import { EntityModules } from 'core/constants';

import routes from 'core/routes';

import EntitiesActivities from 'stores/EntitiesActivities';

import RootStore from '../Root';
import EntityDetails from '../EntityDetails/EntityDetails';

export default class ControlsDetailsStore extends EntityDetails {
  @observable deleteBlockedReason?: string;

  @observable putInPlace: ControlPutInPlace | null = null;

  @observable evidence: ControlEvidenceRow[] | null = null;

  private putInPlaceRequest?: CancelTokenSource;

  private evidenceRequest?: CancelTokenSource;

  /** Bumped on every control write in any tab of this browser; watch it to refetch control lists. */
  @observable libraryVersion = 0;

  private loadingControlId?: string;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api, EntityModules.Controls, routes.dashboard.risks.controls.toString(), (uuid: string): string =>
      reverse(routes.dashboard.risks.controlDetails, { entityId: uuid })
    );

    makeObservable(this);

    eventBus.register(this);
  }

  @action.bound
  async loadDetails(controlId: string): Promise<void> {
    this.isLoaded = false;
    this.deleteBlockedReason = undefined;
    this.loadingControlId = controlId;
    try {
      const { data } = await this.api.entities.loadEntitiesDetails(this.entityType, controlId)();
      if (this.loadingControlId !== controlId) {
        return;
      }
      runInAction(() => {
        this.activities = new EntitiesActivities(this.store, this.api, controlId, this.entityType);
        this.error = undefined;
        this.details = data;
        this.putInPlace = null;
      });
      this.loadPutInPlace(controlId);
    } catch (e) {
      if (this.loadingControlId !== controlId) {
        return;
      }
      runInAction(() => {
        this.error = e as AxiosError;
      });
      if (!isNotFound(e as AxiosError)) {
        Logger.error(`Invalid load control details API response. Control id: ${controlId}`, e);
      }
    } finally {
      if (this.loadingControlId === controlId) {
        runInAction(() => {
          this.isLoaded = true;
        });
      }
    }
  }

  @action.bound
  async loadPutInPlace(controlId: string): Promise<void> {
    this.putInPlaceRequest?.cancel();
    const source = axios.CancelToken.source();
    this.putInPlaceRequest = source;
    try {
      const { data } = await this.api.risks.loadControlPutInPlace(controlId, source)();
      runInAction(() => {
        if (this.details?.uuid === controlId) {
          this.putInPlace = data.putInPlace;
        }
      });
    } catch (e) {
      if (!axios.isCancel(e)) {
        Logger.error(`Failed to load how control ${controlId} was put in place`, e);
      }
    }
  }

  @action.bound
  async loadEvidence(controlId: string): Promise<boolean> {
    this.evidenceRequest?.cancel();
    const source = axios.CancelToken.source();
    this.evidenceRequest = source;
    this.evidence = null;
    try {
      const { data } = await this.api.risks.loadControlEvidence(controlId, source)();
      runInAction(() => {
        if (this.details?.uuid === controlId) {
          this.evidence = data;
        }
      });
      return true;
    } catch (e) {
      if (axios.isCancel(e)) {
        return true;
      }
      Logger.error(`Failed to load the evidence for control ${controlId}`, e);
      runInAction(() => {
        if (this.details?.uuid === controlId) {
          this.evidence = [];
        }
      });
      return false;
    }
  }

  cancelEvidence(): void {
    this.evidenceRequest?.cancel();
  }

  create = (name: string): Promise<boolean | string> => this.createControl(name);

  createControl = async (
    name: string,
    values: Record<string, string | number | string[] | null> = {}
  ): Promise<boolean | string> => {
    runInAction(() => {
      this.fieldErrors = {};
    });
    try {
      const {
        data: { uuid },
      } = await this.api.entities.createEntity(this.entityType, { [`${this.prefix}name`]: name, ...values })();
      this.announceLibraryChange(uuid);
      this.store.notification.enqueueSuccessSnackbar('Control added');
      return uuid;
    } catch (e) {
      const fieldErrors = fieldErrorsOf(e);
      runInAction(() => {
        this.fieldErrors = fieldErrors;
      });
      if (Object.keys(fieldErrors).length === 0) {
        Logger.error('Failed to create control', e);
        this.store.notification.enqueueErrorSnackbar('The control could not be added. Something went wrong.');
      }
      return false;
    }
  };

  delete = async (controlId: string): Promise<boolean> => {
    runInAction(() => {
      this.deleteBlockedReason = undefined;
    });
    try {
      await this.api.entities.deleteEntity(this.entityType, controlId)();
    } catch (e) {
      if (isConflict(e as AxiosError)) {
        runInAction(() => {
          this.deleteBlockedReason = conflictDetail(e) || 'This control is in use.';
        });
      } else {
        Logger.error('Failed to delete control', e);
      }
      return false;
    }
    this.announceLibraryChange(controlId);
    return true;
  };

  edit = async (data: Record<string, EntityValueType>): Promise<boolean | string[]> => {
    if (this.details?.uuid === undefined) {
      return false;
    }
    const controlId = this.details.uuid;
    runInAction(() => {
      this.fieldErrors = {};
    });
    try {
      const edited = await this.api.entities.editEntity(this.entityType, controlId, data)();
      runInAction(() => {
        if (this.details?.uuid === controlId) {
          this.details = edited.data;
          this.isContentEdited = false;
        }
      });
      this.loadPutInPlace(controlId);
      this.announceLibraryChange(controlId);
      this.store.notification.enqueueSuccessSnackbar('Control edited successfully');
      return true;
    } catch (e) {
      const fieldErrors = fieldErrorsOf(e);
      if (Object.keys(fieldErrors).length > 0) {
        runInAction(() => {
          this.fieldErrors = fieldErrors;
        });
        return Object.keys(fieldErrors);
      }
      if (isConflict(e as AxiosError)) {
        this.store.notification.enqueueErrorSnackbar(
          conflictDetail(e) || "An archived control can't be edited. Unarchive it first."
        );
        await this.loadDetails(controlId);
        return false;
      }
      Logger.error('Failed to edit control', e);
      this.store.notification.enqueueErrorSnackbar('The control could not be updated. Something went wrong.');
      return false;
    }
  };

  archiveControl = async (controlId: string, archived: boolean): Promise<{ archivedAt: string | null } | undefined> => {
    try {
      const { data } = await (archived
        ? this.api.entities.archiveEntity(EntityModules.Controls, controlId)()
        : this.api.entities.unarchiveEntity(EntityModules.Controls, controlId)());
      this.announceLibraryChange(controlId);
      this.store.notification.enqueueSuccessSnackbar(archived ? 'Control archived' : 'Control unarchived');
      return data;
    } catch (e) {
      Logger.error('Failed to change the archived state of a control', e);
      this.store.notification.enqueueErrorSnackbar(
        `The control could not be ${archived ? 'archived' : 'unarchived'}. Something went wrong.`
      );
      return undefined;
    }
  };

  @action.bound
  async setArchived(archived: boolean): Promise<boolean> {
    if (this.details?.uuid === undefined) {
      return false;
    }
    const controlId = this.details.uuid;
    const data = await this.archiveControl(controlId, archived);
    if (!data) {
      return false;
    }
    runInAction(() => {
      if (this.details?.uuid === controlId) {
        this.details = { ...this.details, archivedAt: data.archivedAt };
      }
    });
    return true;
  }

  private announceLibraryChange(uuid: string): void {
    postMessage<ChangedLibraryControlsPayload>(EventType.ChangedLibraryControls, { uuid });
  }

  @subscribe(EventType.ChangedLibraryControls)
  @action
  bumpLibraryVersion(): void {
    this.libraryVersion += 1;
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
    this.fieldErrors = {};
    this.deleteBlockedReason = undefined;
    this.evidence = null;
  }

  @action.bound
  contentHasBeenEdited(questionId: string, newValue: AnswerTypes): void {
    const question = this.details?.entity[questionId];
    this.isContentEdited = question?.value !== newValue;
  }
}
